// frontend/src/pages/POS/POS.js - COMPLETE WITH FIXED BULK SALE SERIAL SELECTION
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Badge,
  Autocomplete,
  Modal,
  Fade,
  Backdrop,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  LocalOffer as LocalOfferIcon,
  Payment as PaymentIcon,
  Calculate as CalculateIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon,
  Block as InactiveIcon,
  AccountBalance as ChequeIcon,
  Verified as WarrantyIcon,
  ConfirmationNumber as SerialIcon,
  CheckCircle as PaidIcon,
  Pending as PendingIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { 
  getProducts, 
  getPOSStats, 
  createPOS,
  getCustomers,
  createCustomer,
  getProductSerials,
  reserveSerials
} from '../../services/api';
import { printReceipt, getCurrentCashierName, isInvalidCashierName } from '../../utils/receiptPrint';
import { getCurrentUserName, getCurrentUser } from '../../utils/auth';
import { countAvailableSerials, getNonSerialStock, isMixedSerialProduct } from '../../utils/serialStock';
import './POS.css';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

/** Cashier identity to persist on every POS create (must match logged-in seller) */
const getPosCashierPayload = () => {
  const user = getCurrentUser() || {};
  const name = getCurrentUserName();
  return {
    user_id: user.id ? parseInt(user.id, 10) : undefined,
    user_name: name,
    cashier_name: name,
    username: user.username || undefined,
    email: user.email || undefined
  };
};

// ============ COMPLETE FIXED Serial Number Selection Modal with Working Multi-Select ============
const SerialSelectionModal = ({ 
  open, 
  onClose, 
  product, 
  onSelect,
  quantity 
}) => {
  const [serials, setSerials] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serialSearchTerm, setSerialSearchTerm] = useState('');

  useEffect(() => {
    if (open && product) {
      fetchAvailableSerials();
    } else {
      setSelectedSerials([]);
      setSerialSearchTerm('');
      setError(null);
    }
  }, [open, product, quantity]);

  const filteredSerials = serials.filter(serial => {
    if (!serialSearchTerm.trim()) return true;
    const term = serialSearchTerm.toLowerCase();
    return (
      (serial.serial_number || '').toLowerCase().includes(term) ||
      (serial.current_location || '').toLowerCase().includes(term)
    );
  });

  const fetchAvailableSerials = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProductSerials(product.id, { status: 'available' });
      
      if (response && response.success) {
        const availableSerials = response.data || [];
        setSerials(availableSerials);
        
        if (availableSerials.length < quantity) {
          setError(`Only ${availableSerials.length} good serials available, but you need ${quantity}`);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load serial numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSerial = (serial) => {
    setSelectedSerials(prev => {
      const isSelected = prev.some(s => s.id === serial.id);
      
      if (isSelected) {
        return prev.filter(s => s.id !== serial.id);
      } else {
        if (prev.length < quantity) {
          return [...prev, serial];
        } else {
          setError(`You can only select ${quantity} serial numbers`);
          setTimeout(() => setError(null), 3000);
          return prev;
        }
      }
    });
  };

  const handleSelectAll = () => {
    const visible = filteredSerials;
    const allVisibleSelected = visible.length > 0 && visible.every(s => selectedSerials.some(sel => sel.id === s.id));
    if (allVisibleSelected) {
      setSelectedSerials(prev => prev.filter(s => !visible.some(v => v.id === s.id)));
    } else {
      const maxToSelect = Math.min(visible.length, quantity);
      const slotsLeft = quantity - selectedSerials.length;
      const serialsToAdd = visible.filter(s => !selectedSerials.some(sel => sel.id === s.id)).slice(0, Math.min(maxToSelect, slotsLeft));
      setSelectedSerials(prev => [...prev, ...serialsToAdd]);
    }
  };

  const handleConfirm = () => {
    if (selectedSerials.length === quantity) {
      onSelect(selectedSerials);
      onClose();
    } else {
      setError(`Please select exactly ${quantity} serial numbers. Currently selected: ${selectedSerials.length}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SerialIcon color="primary" />
          <Typography variant="h6">
            Select Serial Numbers for {product?.name}
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          Please select exactly {quantity} serial number(s) from {serials.length} available
          {serialSearchTerm.trim() ? ` (${filteredSerials.length} matching search)` : ''}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search serial number or location..."
          value={serialSearchTerm}
          onChange={(e) => setSerialSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: serialSearchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSerialSearchTerm('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            Selected: <strong>{selectedSerials.length}</strong> of {quantity}
          </Typography>
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={filteredSerials.length === 0}
          >
            {filteredSerials.length > 0 && filteredSerials.every(s => selectedSerials.some(sel => sel.id === s.id))
              ? 'Deselect Visible'
              : `Select Visible (${Math.min(filteredSerials.length, quantity - selectedSerials.length)})`}
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : serials.length === 0 ? (
          <Alert severity="warning">
            No available good serial numbers found for this product.
          </Alert>
        ) : filteredSerials.length === 0 ? (
          <Alert severity="info">
            No serial numbers match &quot;{serialSearchTerm}&quot;.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        filteredSerials.some(s => selectedSerials.some(sel => sel.id === s.id)) &&
                        !filteredSerials.every(s => selectedSerials.some(sel => sel.id === s.id))
                      }
                      checked={filteredSerials.length > 0 && filteredSerials.every(s => selectedSerials.some(sel => sel.id === s.id))}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Warranty Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSerials.map((serial) => {
                  const isSelected = selectedSerials.some(s => s.id === serial.id);
                  const isDisabled = !isSelected && selectedSerials.length >= quantity;
                  
                  return (
                    <TableRow 
                      key={serial.id}
                      hover
                      onClick={() => !isDisabled && handleToggleSerial(serial)}
                      sx={{ 
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isSelected ? '#e3f2fd' : 'inherit',
                        opacity: isDisabled ? 0.5 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => handleToggleSerial(serial)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SerialIcon fontSize="small" />}
                          label={serial.serial_number}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>{serial.current_location || 'Main Warehouse'}</TableCell>
                      <TableCell>
                        {serial.warranty_expiry ? new Date(serial.warranty_expiry).toLocaleDateString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={selectedSerials.length !== quantity}
          color="primary"
        >
          Confirm Selection ({selectedSerials.length}/{quantity})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ FIXED: Bulk Serial Selection Modal ============
const BulkSerialSelectionModal = ({ 
  open, 
  onClose, 
  product, 
  onSelect,
  quantity 
}) => {
  const [serials, setSerials] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serialSearchTerm, setSerialSearchTerm] = useState('');

  useEffect(() => {
    if (open && product) {
      fetchAvailableSerials();
    } else {
      setSelectedSerials([]);
      setSerialSearchTerm('');
      setError(null);
    }
  }, [open, product, quantity]);

  const filteredSerials = serials.filter(serial => {
    if (!serialSearchTerm.trim()) return true;
    const term = serialSearchTerm.toLowerCase();
    return (
      (serial.serial_number || '').toLowerCase().includes(term) ||
      (serial.current_location || '').toLowerCase().includes(term)
    );
  });

  const fetchAvailableSerials = async () => {
    try {
      setLoading(true);
      const response = await getProductSerials(product.id, { status: 'available' });
      
      if (response && response.success) {
        const availableSerials = response.data || [];
        setSerials(availableSerials);
        
        if (availableSerials.length < quantity) {
          setError(`Only ${availableSerials.length} good serials available for this bulk sale, but you need ${quantity}`);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load serial numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSerial = (serial) => {
    setSelectedSerials(prev => {
      const isSelected = prev.some(s => s.id === serial.id);
      
      if (isSelected) {
        return prev.filter(s => s.id !== serial.id);
      } else {
        if (prev.length < quantity) {
          return [...prev, serial];
        } else {
          setError(`You can only select ${quantity} serial numbers for this product`);
          setTimeout(() => setError(null), 3000);
          return prev;
        }
      }
    });
  };

  const handleSelectAll = () => {
    const visible = filteredSerials;
    const allVisibleSelected = visible.length > 0 && visible.every(s => selectedSerials.some(sel => sel.id === s.id));
    if (allVisibleSelected) {
      setSelectedSerials(prev => prev.filter(s => !visible.some(v => v.id === s.id)));
    } else {
      const maxToSelect = Math.min(visible.length, quantity);
      const slotsLeft = quantity - selectedSerials.length;
      const serialsToAdd = visible.filter(s => !selectedSerials.some(sel => sel.id === s.id)).slice(0, Math.min(maxToSelect, slotsLeft));
      setSelectedSerials(prev => [...prev, ...serialsToAdd]);
    }
  };

  const handleConfirm = () => {
    if (selectedSerials.length === quantity) {
      onSelect(selectedSerials);
      onClose();
    } else {
      setError(`Please select exactly ${quantity} serial number(s). Currently selected: ${selectedSerials.length}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SerialIcon color="secondary" />
          <Typography variant="h6">
            Select Serial Numbers for Bulk Sale: {product?.name}
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          Please select exactly {quantity} serial number(s), then click OK
          {serialSearchTerm.trim() ? ` (${filteredSerials.length} matching search)` : ''}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search serial number or location..."
          value={serialSearchTerm}
          onChange={(e) => setSerialSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: serialSearchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSerialSearchTerm('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            Selected: <strong>{selectedSerials.length}</strong> of {quantity}
          </Typography>
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={filteredSerials.length === 0}
          >
            {filteredSerials.length > 0 && filteredSerials.every(s => selectedSerials.some(sel => sel.id === s.id))
              ? 'Deselect Visible'
              : `Select Visible (${Math.min(filteredSerials.length, quantity - selectedSerials.length)})`}
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : serials.length === 0 ? (
          <Alert severity="warning">
            No available good serial numbers found for this product.
          </Alert>
        ) : filteredSerials.length === 0 ? (
          <Alert severity="info">
            No serial numbers match &quot;{serialSearchTerm}&quot;.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '48vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        filteredSerials.some(s => selectedSerials.some(sel => sel.id === s.id)) &&
                        !filteredSerials.every(s => selectedSerials.some(sel => sel.id === s.id))
                      }
                      checked={filteredSerials.length > 0 && filteredSerials.every(s => selectedSerials.some(sel => sel.id === s.id))}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Warranty Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSerials.map((serial) => {
                  const isSelected = selectedSerials.some(s => s.id === serial.id);
                  const isDisabled = !isSelected && selectedSerials.length >= quantity;
                  
                  return (
                    <TableRow 
                      key={serial.id}
                      hover
                      onClick={() => !isDisabled && handleToggleSerial(serial)}
                      sx={{ 
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isSelected ? '#fff3e0' : 'inherit',
                        opacity: isDisabled ? 0.5 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled && !isSelected}
                          onChange={() => handleToggleSerial(serial)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SerialIcon fontSize="small" />}
                          label={serial.serial_number}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>{serial.current_location || 'Main Warehouse'}</TableCell>
                      <TableCell>
                        {serial.warranty_expiry ? new Date(serial.warranty_expiry).toLocaleDateString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={selectedSerials.length !== quantity}
          color="secondary"
        >
          OK ({selectedSerials.length}/{quantity})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Generate unique invoice number ============
const generateInvoiceNumber = () => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  
  const storageKey = `pos_invoice_counter_${dateStr}`;
  let counter = parseInt(localStorage.getItem(storageKey)) || 0;
  
  counter++;
  localStorage.setItem(storageKey, counter);
  
  const sequence = counter.toString().padStart(3, '0');
  const invoiceNumber = `INV-${dateStr}-${sequence}`;
  localStorage.setItem('last_invoice_number', invoiceNumber);
  
  return invoiceNumber;
};

// ============ Credit Payment Modal ============
const CreditPaymentModal = ({ 
  open, 
  onClose, 
  customer, 
  amount, 
  onSuccess,
  onCreateCreditSale,
  invoiceNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [paymentData, setPaymentData] = useState({
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: ''
  });

  useEffect(() => {
    if (open) {
      setPaymentData({
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: ''
      });
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleCreditSale = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await onCreateCreditSale(customer, paymentData, amount, invoiceNumber);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess(response.data, invoiceNumber, paymentData.dueDate);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to process credit sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CreditCardIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Process Credit Payment
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Credit sale processed successfully!
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Customer: <strong>{customer.name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Code: {customer.customer_code || 'N/A'}
          </Typography>
          <Typography variant="body2" color="primary" gutterBottom>
            Invoice #: <strong>{invoiceNumber}</strong>
          </Typography>
          
          <Box sx={{ my: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', color: '#e74c3c' }}>
              Amount: {formatCurrency(amount)}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Due Date"
            type="date"
            value={paymentData.dueDate}
            onChange={(e) => setPaymentData({...paymentData, dueDate: e.target.value})}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled={loading || success}
          />
          
          <TextField
            fullWidth
            label="Description (Optional)"
            value={paymentData.description}
            onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
            margin="normal"
            multiline
            rows={2}
            disabled={loading || success}
            placeholder="e.g., POS transaction for invoice"
          />
          
          {customer.available_credit !== undefined && customer.available_credit < amount && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
              <Typography variant="body2">
                Customer's available credit ({formatCurrency(customer.available_credit)}) is less than the transaction amount.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleCreditSale} 
          variant="contained" 
          disabled={loading || success || (customer.available_credit !== undefined && customer.available_credit < amount)}
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : success ? 'âœ“' : <CreditCardIcon />}
        >
          {loading ? 'Processing...' : success ? 'Success!' : 'Confirm Credit Sale'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Cheque Payment Modal Component ============
const ChequePaymentModal = ({ 
  open, 
  onClose, 
  amount, 
  onProcessChequePayment,
  customer,
  invoiceNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [chequeData, setChequeData] = useState({
    chequeNumber: '',
    bankName: '',
    branch: '',
    chequeDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isPostDated: false,
    notes: `Payment for invoice ${invoiceNumber}`
  });

  useEffect(() => {
    if (open) {
      setChequeData({
        chequeNumber: '',
        bankName: '',
        branch: '',
        chequeDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isPostDated: false,
        notes: `Payment for invoice ${invoiceNumber}`
      });
      setError(null);
      setSuccess(false);
    }
  }, [open, invoiceNumber]);

  const handleChequePayment = async () => {
    if (!chequeData.chequeNumber.trim()) {
      setError('Cheque number is required');
      return;
    }
    
    if (!chequeData.bankName.trim()) {
      setError('Bank name is required');
      return;
    }

    if (!chequeData.chequeDate) {
      setError('Cheque date is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await onProcessChequePayment(chequeData);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to process cheque payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ChequeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Cheque Payment Details
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Cheque payment details saved successfully!
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Customer: <strong>{customer?.name || 'Walk-in Customer'}</strong>
          </Typography>
          <Typography variant="body2" color="primary" gutterBottom>
            Invoice #: <strong>{invoiceNumber}</strong>
          </Typography>
          
          <Box sx={{ my: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', color: '#2ecc71' }}>
              Amount: {formatCurrency(amount)}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cheque Number *"
                value={chequeData.chequeNumber}
                onChange={(e) => setChequeData({...chequeData, chequeNumber: e.target.value})}
                disabled={loading || success}
                placeholder="Enter cheque number"
                required
                error={!chequeData.chequeNumber.trim() && error !== null}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name *"
                value={chequeData.bankName}
                onChange={(e) => setChequeData({...chequeData, bankName: e.target.value})}
                disabled={loading || success}
                placeholder="e.g., Commercial Bank"
                required
                error={!chequeData.bankName.trim() && error !== null}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch"
                value={chequeData.branch}
                onChange={(e) => setChequeData({...chequeData, branch: e.target.value})}
                disabled={loading || success}
                placeholder="e.g., Colombo"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cheque Date *"
                type="date"
                value={chequeData.chequeDate}
                onChange={(e) => setChequeData({...chequeData, chequeDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                disabled={loading || success}
                required
                error={!chequeData.chequeDate && error !== null}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={chequeData.dueDate}
                onChange={(e) => setChequeData({...chequeData, dueDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                disabled={loading || success}
                helperText="When cheque can be cashed"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={chequeData.isPostDated}
                    onChange={(e) => setChequeData({...chequeData, isPostDated: e.target.checked})}
                    disabled={loading || success}
                  />
                }
                label="Post-dated Cheque"
              />
              {chequeData.isPostDated && (
                <Typography variant="caption" color="warning.main" display="block">
                  Note: Post-dated cheque will be held until due date
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                value={chequeData.notes}
                onChange={(e) => setChequeData({...chequeData, notes: e.target.value})}
                multiline
                rows={2}
                disabled={loading || success}
                placeholder="Additional notes about this cheque"
              />
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Verify cheque details before accepting. 
              The sale will be recorded as pending until cheque is cleared.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleChequePayment} 
          variant="contained" 
          disabled={loading || success || !chequeData.chequeNumber.trim() || !chequeData.bankName.trim() || !chequeData.chequeDate}
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : success ? 'âœ“' : <ChequeIcon />}
        >
          {loading ? 'Processing...' : success ? 'Success!' : 'Confirm Cheque Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Payment Status Modal Component ============
const PaymentStatusModal = ({ 
  open, 
  onClose, 
  onConfirm,
  transactionData
}) => {
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [loading, setLoading] = useState(false);

  const handleConfirm = () => {
    setLoading(true);
    setTimeout(() => {
      onConfirm(paymentStatus);
      setLoading(false);
      onClose();
    }, 500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Payment Status
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please select the payment status for this receipt
            </Typography>
          </Alert>
          
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend">Receipt Status</FormLabel>
            <RadioGroup
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
            >
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  border: paymentStatus === 'paid' ? '2px solid #2e7d32' : '1px solid #e0e0e0',
                  bgcolor: paymentStatus === 'paid' ? '#e8f5e8' : 'transparent',
                  cursor: 'pointer'
                }}
                onClick={() => setPaymentStatus('paid')}
              >
                <FormControlLabel 
                  value="paid" 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center" gap={2}>
                      <PaidIcon sx={{ color: '#2e7d32' }} />
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="#2e7d32">
                          PAID
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Transaction completed, payment received
                        </Typography>
                      </Box>
                    </Box>
                  } 
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
              
              <Paper 
                sx={{ 
                  p: 2, 
                  border: paymentStatus === 'pending' ? '2px solid #f57c00' : '1px solid #e0e0e0',
                  bgcolor: paymentStatus === 'pending' ? '#fff3e0' : 'transparent',
                  cursor: 'pointer'
                }}
                onClick={() => setPaymentStatus('pending')}
              >
                <FormControlLabel 
                  value="pending" 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center" gap={2}>
                      <PendingIcon sx={{ color: '#f57c00' }} />
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="#f57c00">
                          PENDING
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Awaiting payment or credit/cheque clearance
                        </Typography>
                      </Box>
                    </Box>
                  } 
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            </RadioGroup>
          </FormControl>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Invoice Summary:
            </Typography>
            <Typography variant="body2">
              Invoice #: <strong>{transactionData?.receipt_number || transactionData?.pos_number || 'N/A'}</strong>
            </Typography>
            <Typography variant="body2">
              Amount: <strong>{formatCurrency(transactionData?.total || 0)}</strong>
            </Typography>
            <Typography variant="body2">
              Customer: <strong>{transactionData?.customer || 'Walk-in Customer'}</strong>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={loading}
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ReceiptIcon />}
        >
          {loading ? 'Processing...' : 'Print Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Quantity Dialog Component with Auto-focus and Keyboard Support ============
const QuantityDialog = ({ 
  open, 
  onClose, 
  product, 
  onConfirm,
  availableStock,
  serialStock = 0,
  nonSerialStock = 0
}) => {
  const [quantity, setQuantity] = useState(1);
  const [sellMode, setSellMode] = useState('non-serial');
  const inputRef = useRef(null);
  const mixed = serialStock > 0 && nonSerialStock > 0;
  const maxQty = mixed
    ? (sellMode === 'serial' ? serialStock : nonSerialStock)
    : (serialStock > 0 && nonSerialStock === 0 ? serialStock : availableStock);

  useEffect(() => {
    if (open) {
      const nextMode = serialStock > 0 && nonSerialStock === 0
        ? 'serial'
        : (mixed ? 'serial' : 'non-serial');
      setSellMode(nextMode);
      setQuantity(1);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
    }
  }, [open, serialStock, nonSerialStock, mixed]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  const handleConfirm = () => {
    if (quantity > 0 && quantity <= maxQty) {
      onConfirm(quantity, sellMode);
      onClose();
    }
  };

  const handleModeChange = (_, next) => {
    if (!next) return;
    setSellMode(next);
    const cap = next === 'serial' ? serialStock : nonSerialStock;
    setQuantity((q) => Math.min(q, Math.max(1, cap)));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          <Typography variant="h6">
            Select Quantity
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {product && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{product.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Code: {product.product_code} | Price: {formatCurrency(product.price)}
            </Typography>
            <Typography variant="body2" color={availableStock <= 10 ? 'warning.main' : 'success.main'} gutterBottom>
              Available Good Stock: {availableStock} units
              {mixed && ` · Serial: ${serialStock} · Non-serial: ${nonSerialStock}`}
            </Typography>

            {mixed && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>This product has serial and non-serial stock. Choose how to sell:</Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={sellMode}
                  onChange={handleModeChange}
                >
                  <ToggleButton value="serial">Serial</ToggleButton>
                  <ToggleButton value="non-serial">Non-serial</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <IconButton 
                onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                sx={{ bgcolor: '#f0f0f0' }}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                inputRef={inputRef}
                type="number"
                value={quantity}
                onChange={(e) => {
                  let val = parseInt(e.target.value) || 1;
                  val = Math.min(maxQty, Math.max(1, val));
                  setQuantity(val);
                }}
                onKeyPress={handleKeyPress}
                InputProps={{
                  inputProps: { 
                    min: 1, 
                    max: maxQty, 
                    style: { textAlign: 'center', fontSize: '20px', fontWeight: 'bold' } 
                  }
                }}
                sx={{ width: 100, '& input': { textAlign: 'center' } }}
              />
              <IconButton 
                onClick={() => setQuantity(prev => Math.min(maxQty, prev + 1))}
                sx={{ bgcolor: '#f0f0f0' }}
              >
                <AddIcon />
              </IconButton>
            </Box>
            
            {((mixed && sellMode === 'serial') || (!mixed && serialStock > 0 && nonSerialStock === 0)) && quantity > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <SerialIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  You will select {quantity} serial number{quantity === 1 ? '' : 's'} for this product.
                </Typography>
              </Alert>
            )}
            {((mixed && sellMode === 'non-serial') || serialStock === 0) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Selling without serial numbers.
                </Typography>
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Tip: Type the quantity directly and press Enter
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary"
          disabled={!product || quantity <= 0 || maxQty <= 0}
          startIcon={<AddIcon />}
        >
          Add to Cart ({quantity} {quantity === 1 ? 'item' : 'items'})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ MAIN POS COMPONENT ============
const POS = () => {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState({ 
    id: 1, 
    name: 'Walk-in Customer',
    is_credit_customer: false,
    credit_limit: 0,
    available_credit: 0
  });
  const [discount, setDiscount] = useState({ type: 'percentage', value: 0 });
  const [payment, setPayment] = useState({
    method: 'cash',
    amountPaid: '',
    change: 0,
    chequeDetails: null
  });
  const [taxRate, setTaxRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    credit_limit: 0,
    customer_type: 'individual'
  });
  const [creditPaymentOpen, setCreditPaymentOpen] = useState(false);
  const [selectedCreditCustomer, setSelectedCreditCustomer] = useState(null);
  
  // Quantity Dialog State
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [selectedProductForQuantity, setSelectedProductForQuantity] = useState(null);
  
  const [serialSelectionOpen, setSerialSelectionOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQuantity, setPendingQuantity] = useState(1);
  const [selectedSerials, setSelectedSerials] = useState([]);
  
  const [bulkSerialSelectionOpen, setBulkSerialSelectionOpen] = useState(false);
  const [pendingBulkProduct, setPendingBulkProduct] = useState(null);
  const [pendingBulkQuantity, setPendingBulkQuantity] = useState(1);
  const [pendingBulkSerials, setPendingBulkSerials] = useState([]);
  
  const [pendingBulkProducts, setPendingBulkProducts] = useState([]);
  const [pendingBulkPrice, setPendingBulkPrice] = useState('');
  const [pendingBulkProductIndex, setPendingBulkProductIndex] = useState(0);
  const [pendingBulkSelectedSerials, setPendingBulkSelectedSerials] = useState({});
  
  const [invoiceNumber, setInvoiceNumber] = useState('');
  
  const [bulkSaleDialogOpen, setBulkSaleDialogOpen] = useState(false);
  const [selectedBulkProducts, setSelectedBulkProducts] = useState([]);
  const [searchBulkTerm, setSearchBulkTerm] = useState('');
  const [bulkSalePrice, setBulkSalePrice] = useState('');
  
  const [chequePaymentOpen, setChequePaymentOpen] = useState(false);
  const [paymentStatusModalOpen, setPaymentStatusModalOpen] = useState(false);
  const [pendingReceiptData, setPendingReceiptData] = useState(null);
  
  const barcodeInputRef = useRef(null);
  const navigate = useNavigate();

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0);
  const discountAmount = discount.type === 'percentage' 
    ? subtotal * (discount.value / 100)
    : discount.value;
  const taxAmount = (subtotal - discountAmount) * (taxRate / 100);
  const totalAmount = subtotal - discountAmount + taxAmount;

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchStats();
    setInvoiceNumber(generateInvoiceNumber());
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (cart.length === 0 && !paymentDialogOpen && !creditPaymentOpen && !chequePaymentOpen && !paymentStatusModalOpen) {
      const newInvoiceNumber = generateInvoiceNumber();
      setInvoiceNumber(newInvoiceNumber);
    }
  }, [cart, paymentDialogOpen, creditPaymentOpen, chequePaymentOpen, paymentStatusModalOpen]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getProducts();
      if (response.success) {
        console.log('ðŸ“¦ Products loaded:', response.data.length);
        setProducts(response.data);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      if (response.success) {
        let allCustomers = response.data;
        
        allCustomers = allCustomers.map(customer => {
          const creditLimit = parseFloat(customer.credit_limit) || 0;
          const outstanding = parseFloat(customer.outstanding_balance) || 0;
          const available = parseFloat(customer.available_credit) || (creditLimit - outstanding);
          
          const isCreditCustomer = customer.is_credit_customer === true || creditLimit > 0;
          
          return {
            ...customer,
            is_credit_customer: isCreditCustomer,
            credit_limit: creditLimit,
            available_credit: available,
            outstanding_balance: outstanding,
            credit_utilization: creditLimit > 0 ? Math.round((outstanding / creditLimit) * 100) : 0
          };
        });
        
        setCustomers(allCustomers);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setCustomers([{
        id: 1,
        name: 'Walk-in Customer',
        phone: 'N/A',
        is_credit_customer: false,
        credit_limit: 0,
        available_credit: 0,
        outstanding_balance: 0
      }]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getPOSStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching POS stats:', err);
      setStats({
        today: { sales: '0.00', transactions: 0 },
        monthly: { sales: '0.00' }
      });
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      const term = searchTerm.trim();
      const product = products.find(p => 
        p.product_code === term || 
        (p.barcode && p.barcode === term) ||
        (p.serials && Array.isArray(p.serials) && p.serials.some(s => s.serial_number === term))
      );
      
      if (product) {
        if (product.is_active === false) {
          setError('Cannot add inactive product to cart');
          setTimeout(() => setError(''), 3000);
        } else {
          const availableGoodStock = Math.max(0, product.good_quantity || 0);
          if (availableGoodStock <= 0) {
            setError(`No good stock available for this product. Good stock: ${availableGoodStock}`);
            setTimeout(() => setError(''), 3000);
          } else {
            const matchedSerial = (product.serials || []).find(
              (s) => s.serial_number === term && (s.status === 'available' || s.status === 'good')
            );
            if (matchedSerial) {
              const newItem = createCartItem(product, 1, [matchedSerial.serial_number], true);
              setCurrentEditItem(newItem);
              setEditItemDialogOpen(true);
            } else {
              addToCart(product, 1);
            }
            setSearchTerm('');
          }
        }
      } else {
        setError('Product not found!');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const remainingSerialStock = (product) => {
    if (!product) return 0;
    const used = cart
      .filter((i) => i.product_id === product.id && i.sell_with_serials)
      .reduce((s, i) => s + (parseInt(i.quantity, 10) || 0), 0);
    return Math.max(0, countAvailableSerials(product) - used);
  };

  const remainingNonSerialStock = (product) => {
    if (!product) return 0;
    const used = cart
      .filter((i) => i.product_id === product.id && !i.sell_with_serials)
      .reduce((s, i) => s + (parseInt(i.quantity, 10) || 0), 0);
    return Math.max(0, getNonSerialStock(product) - used);
  };

  const shouldPickSerials = (product, sellMode) => {
    if (sellMode === 'non-serial') return false;
    if (sellMode === 'serial') return remainingSerialStock(product) > 0;
    return remainingSerialStock(product) > 0 && remainingNonSerialStock(product) === 0;
  };

  // ============ UPDATED addToCart function with quantity dialog ============
  const addToCart = (product, quantity = 1) => {
    const availableGoodStock = Math.max(0, product.good_quantity || 0);
    
    if (product.is_active === false) {
      setError('Cannot add inactive product to cart');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (availableGoodStock <= 0) {
      setError(`No good stock available for this product. Good stock: ${availableGoodStock}`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    // If quantity is provided (from bulk add or barcode), use it directly
    if (quantity > 1) {
      if (availableGoodStock < quantity) {
        setError(`Only ${availableGoodStock} good units available. You requested ${quantity}.`);
        setTimeout(() => setError(''), 3000);
        return;
      }

      if (shouldPickSerials(product, isMixedSerialProduct(product) ? 'non-serial' : null)) {
        setPendingProduct(product);
        setPendingQuantity(quantity);
        setSerialSelectionOpen(true);
      } else {
        const newItem = createCartItem(product, quantity, [], false);
        setCurrentEditItem(newItem);
        setEditItemDialogOpen(true);
      }
    } else {
      setSelectedProductForQuantity(product);
      setQuantityDialogOpen(true);
    }
  };

  const handleQuantityConfirm = (quantity, sellMode = 'non-serial') => {
    const product = selectedProductForQuantity;
    const maxForMode = sellMode === 'serial'
      ? remainingSerialStock(product)
      : remainingNonSerialStock(product) || Math.max(0, product.good_quantity || 0);
    
    if (quantity > maxForMode) {
      setError(`Only ${maxForMode} ${sellMode === 'serial' ? 'serial' : 'non-serial'} unit(s) available.`);
      setTimeout(() => setError(''), 3000);
      setQuantityDialogOpen(false);
      return;
    }
    
    if (shouldPickSerials(product, sellMode)) {
      setPendingProduct(product);
      setPendingQuantity(quantity);
      setSerialSelectionOpen(true);
    } else {
      const newItem = createCartItem(product, quantity, [], false);
      setCurrentEditItem(newItem);
      setEditItemDialogOpen(true);
    }
    
    setQuantityDialogOpen(false);
    setSelectedProductForQuantity(null);
  };

  const createCartItem = (product, quantity, serialNumbers, sellWithSerials = null) => {
    const withSerials = sellWithSerials != null
      ? sellWithSerials
      : (Array.isArray(serialNumbers) && serialNumbers.length > 0);
    return {
      id: Date.now(),
      product_id: product.id,
      name: product.name,
      product_name: product.name,
      product_code: product.product_code,
      quantity: quantity,
      unit_price: product.price,
      discount_percentage: 0,
      tax_rate: taxRate,
      line_total: product.price * quantity,
      stock: product.quantity,
      good_stock: Math.max(0, product.good_quantity || 0),
      warranty_months: product.warranty_months || 0,
      warranty_terms: product.warranty_terms || '',
      serial_numbers: serialNumbers,
      track_serials: withSerials,
      sell_with_serials: withSerials
    };
  };

  const handleSerialSelection = (selectedSerials) => {
    setSelectedSerials(selectedSerials);
    
    if (pendingProduct) {
      const serialNumbers = selectedSerials.map(s => s.serial_number);
      const newItem = createCartItem(pendingProduct, pendingQuantity, serialNumbers, true);
      setCurrentEditItem(newItem);
      setEditItemDialogOpen(true);
    }
  };

  const productNeedsSerials = (p) => {
    if (p?.sell_with_serials === false) return false;
    if (p?.sell_with_serials === true) return true;
    return countAvailableSerials(p) > 0 && getNonSerialStock(p) === 0;
  };

  // ============ FIXED: Handle bulk serial selection with multiple products ============
  const handleBulkSerialSelection = (selectedSerials) => {
    const updatedProducts = [...pendingBulkProducts];
    const currentIdx = updatedProducts.findIndex(
      (p) => p.id === (pendingBulkProduct?.id ?? updatedProducts[pendingBulkProductIndex]?.id)
    );
    const idx = currentIdx >= 0 ? currentIdx : pendingBulkProductIndex;
    const currentProduct = updatedProducts[idx];

    let allSerialObjects = [...pendingBulkSerials];

    if (currentProduct) {
      const serialNumbers = selectedSerials.map((s) => s.serial_number);
      const serialObjects = selectedSerials.map((s) => ({
        id: s.id,
        serial_number: s.serial_number,
        product_id: currentProduct.id
      }));

      // Replace any previous serials for this product
      allSerialObjects = [
        ...allSerialObjects.filter((s) => s.product_id !== currentProduct.id),
        ...serialObjects
      ];

      currentProduct.selected_serials = serialNumbers;
      updatedProducts[idx] = currentProduct;
      setPendingBulkProducts(updatedProducts);
      setPendingBulkSerials(allSerialObjects);
    }

    const nextIdx = updatedProducts.findIndex(
      (p, i) =>
        i > idx &&
        productNeedsSerials(p) &&
        (!p.selected_serials || p.selected_serials.length < p.quantity)
    );

    if (nextIdx >= 0) {
      setPendingBulkProduct(updatedProducts[nextIdx]);
      setPendingBulkQuantity(updatedProducts[nextIdx].quantity);
      setPendingBulkProductIndex(nextIdx);
      setBulkSerialSelectionOpen(true);
    } else {
      processBulkSale(updatedProducts, pendingBulkPrice, allSerialObjects);
      setBulkSerialSelectionOpen(false);
      setBulkSaleDialogOpen(false);
      setPendingBulkProducts([]);
      setPendingBulkPrice('');
      setPendingBulkSerials([]);
      setPendingBulkProductIndex(0);
      setPendingBulkSelectedSerials({});
    }
  };

  const updateCartItem = (updatedItem) => {
    setCart(prevCart => {
      const existingIndex = prevCart.findIndex(item => item.id === updatedItem.id);
      
      if (existingIndex >= 0) {
        const newCart = [...prevCart];
        newCart[existingIndex] = updatedItem;
        return newCart;
      } else {
        return [...prevCart, updatedItem];
      }
    });
    setEditItemDialogOpen(false);
    setCurrentEditItem(null);
    setSelectedSerials([]);
  };

  const updateQuantity = (id, delta) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + delta;
          
          if (newQuantity < 1) {
            return item;
          }
          
          if (newQuantity > item.good_stock) {
            setError(`Only ${item.good_stock} good items available in stock`);
            setTimeout(() => setError(''), 3000);
            return item;
          }
          
          if (item.sell_with_serials && delta > 0) {
            setPendingProduct(products.find(p => p.id === item.product_id));
            setPendingQuantity(newQuantity - item.quantity);
            setSerialSelectionOpen(true);
            return item;
          }
          
          const newLineTotal = (item.unit_price * newQuantity) * (1 - (item.discount_percentage / 100));
          
          return { 
            ...item, 
            quantity: newQuantity,
            line_total: newLineTotal
          };
        }
        return item;
      });
    });
  };

  const editItem = (item) => {
    setCurrentEditItem(item);
    setEditItemDialogOpen(true);
  };

  const removeFromCart = (id) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  };

  const handlePaymentChange = (field, value) => {
    if (field === 'method' && value === 'credit') {
      if (selectedCustomer.id === 1) {
        setError('Please select a customer for credit payment');
        setPayment(prev => ({ ...prev, method: 'cash' }));
        return;
      }
      
      const customerData = customers.find(c => c.id === selectedCustomer.id);
      if (!customerData?.is_credit_customer) {
        setError('Selected customer is not approved for credit');
        setPayment(prev => ({ ...prev, method: 'cash' }));
        return;
      }
      
      if (customerData.available_credit < totalAmount) {
        setError(`Customer credit limit exceeded. Available: ${formatCurrency(customerData.available_credit)}`);
        setPayment(prev => ({ ...prev, method: 'cash' }));
        return;
      }
      
      setSelectedCreditCustomer(customerData);
      setCreditPaymentOpen(true);
      return;
    }
    
    if (field === 'method' && value === 'cheque') {
      setPayment(prev => ({
        ...prev,
        method: value,
        amountPaid: totalAmount,
        change: 0,
        chequeDetails: null
      }));
      setChequePaymentOpen(true);
      return;
    }
    
    setPayment(prev => ({
      ...prev,
      [field]: value,
      change: field === 'amountPaid' 
        ? parseFloat(value || 0) - totalAmount 
        : prev.change
    }));
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) {
      setError('Customer name is required');
      return;
    }

    try {
      const customerData = {
        ...newCustomer,
        credit_limit: parseFloat(newCustomer.credit_limit) || 0,
        customer_code: `CUST-${Date.now().toString().slice(-6)}`
      };

      const response = await createCustomer(customerData);
      if (response.success) {
        await fetchCustomers();
        setSelectedCustomer({
          id: response.data.id,
          name: newCustomer.name,
          is_credit_customer: parseFloat(newCustomer.credit_limit) > 0,
          credit_limit: parseFloat(newCustomer.credit_limit) || 0,
          available_credit: parseFloat(newCustomer.credit_limit) || 0
        });
        setNewCustomer({ 
          name: '', 
          phone: '', 
          email: '', 
          address: '',
          credit_limit: 0,
          customer_type: 'individual'
        });
        setCustomerDialogOpen(false);
        setSuccess('Customer added successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to create customer');
    }
  };

  const createCreditSale = async (customer, paymentData, amount, invoiceNumber) => {
    try {
      const posData = {
        customer_id: customer.id,
        customer_name: customer.name,
        ...getPosCashierPayload(),
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name || item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percentage: item.discount_percentage,
          tax_rate: item.tax_rate,
          warranty_months: item.warranty_months,
          warranty_terms: item.warranty_terms,
          serial_numbers: item.serial_numbers || [],
          is_bulk_sale: item.is_bulk_sale || false,
          original_price: item.original_price || null,
          bulk_discount_percentage: item.bulk_discount_percentage || null
        })),
        discount_type: discount.type,
        discount_value: discount.value,
        payment_method: 'credit',
        amount_paid: 0,
        change_amount: 0,
        tax_rate: taxRate,
        total_amount: totalAmount,
        notes: `Credit Sale - Due Date: ${paymentData.dueDate} | ${paymentData.description || 'No description'}`,
        is_credit_sale: true,
        credit_amount: amount,
        credit_due_date: paymentData.dueDate,
        pos_number: invoiceNumber
      };

      const response = await createPOS(posData);
      return response;
    } catch (err) {
      throw err;
    }
  };

  const handleProcessChequePayment = async (chequeData) => {
    console.log('ðŸ’° Processing cheque payment with data:', chequeData);
    
    if (!chequeData || !chequeData.chequeNumber || !chequeData.bankName || !chequeData.chequeDate) {
      setError('Cheque number, bank name, and cheque date are required');
      return;
    }
    
    await processPaymentWithCheque(chequeData);
  };

  const processPaymentWithCheque = async (chequeData) => {
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    setLoading(true);
    
    const currentInvoiceNumber = invoiceNumber;
    
    const posData = {
      customer_id: selectedCustomer.id,
      customer_name: selectedCustomer.name,
      ...getPosCashierPayload(),
      items: cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name || item.name,
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        discount_percentage: parseFloat(item.discount_percentage) || 0,
        tax_rate: parseFloat(item.tax_rate) || 0,
        warranty_months: parseInt(item.warranty_months) || 0,
        warranty_terms: item.warranty_terms || '',
        serial_numbers: item.serial_numbers || [],
        is_bulk_sale: item.is_bulk_sale || false,
        original_price: item.original_price ? parseFloat(item.original_price) : null,
        bulk_discount_percentage: item.bulk_discount_percentage ? parseFloat(item.bulk_discount_percentage) : null
      })),
      discount_type: discount.type,
      discount_value: parseFloat(discount.value) || 0,
      payment_method: 'cheque',
      amount_paid: parseFloat(totalAmount) || 0,
      change_amount: 0,
      tax_rate: parseFloat(taxRate) || 0,
      notes: `Cheque Payment - ${new Date().toLocaleString()}`,
      pos_number: currentInvoiceNumber,
      
      cheque_number: chequeData.chequeNumber || '',
      cheque_bank: chequeData.bankName || '',
      cheque_branch: chequeData.branch || '',
      cheque_date: chequeData.chequeDate || new Date().toISOString().split('T')[0],
      cheque_due_date: chequeData.dueDate || chequeData.chequeDate || new Date().toISOString().split('T')[0],
      is_post_dated_cheque: chequeData.isPostDated || false,
      cheque_status: 'pending',
      cheque_details: {
        chequeNumber: chequeData.chequeNumber || '',
        bankName: chequeData.bankName || '',
        branch: chequeData.branch || '',
        chequeDate: chequeData.chequeDate || '',
        dueDate: chequeData.dueDate || chequeData.chequeDate || '',
        isPostDated: chequeData.isPostDated || false,
        notes: chequeData.notes || `Payment for invoice ${currentInvoiceNumber}`
      }
    };

    console.log('ðŸ“¤ Sending CHEQUE payment data:', JSON.stringify(posData, null, 2));

    try {
      const response = await createPOS(posData);
      
      if (response.success) {
        setSuccess('Cheque payment recorded successfully!');
        
        setChequePaymentOpen(false);
        
        const receiptData = {
          ...response.data,
          user_name: response.data?.user_name || getCurrentCashierName(),
          cashier_name: response.data?.user_name || getCurrentCashierName(),
          payment_method: 'cheque',
          customer: selectedCustomer.name,
          items: cart,
          subtotal: subtotal,
          discount: discountAmount,
          tax: taxAmount,
          total: totalAmount,
          paid: totalAmount,
          change: 0,
          date: new Date().toISOString(),
          receipt_number: currentInvoiceNumber,
          pos_number: currentInvoiceNumber,
          cheque_details: chequeData,
          is_cheque_payment: true,
          status: 'pending'
        };
        
        setPendingReceiptData(receiptData);
        setPaymentStatusModalOpen(true);
        
        setCart([]);
        setPayment({
          method: 'cash',
          amountPaid: '',
          change: 0,
          chequeDetails: null
        });
        
        setPaymentDialogOpen(false);
        
        const newInvoiceNumber = generateInvoiceNumber();
        setInvoiceNumber(newInvoiceNumber);
        
        await refreshAfterSale();
        
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('âŒ Cheque transaction failed:', err);
      setError(err.message || 'Cheque transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    if (payment.method === 'cheque') {
      return;
    }

    if (payment.method !== 'credit' && parseFloat(payment.amountPaid || 0) < totalAmount) {
      setError('Insufficient payment amount');
      return;
    }

    setLoading(true);
    
    const currentInvoiceNumber = invoiceNumber;
    
    const posData = {
      customer_id: selectedCustomer.id,
      customer_name: selectedCustomer.name,
      ...getPosCashierPayload(),
      items: cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name || item.name,
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        discount_percentage: parseFloat(item.discount_percentage) || 0,
        tax_rate: parseFloat(item.tax_rate) || 0,
        warranty_months: parseInt(item.warranty_months) || 0,
        warranty_terms: item.warranty_terms || '',
        serial_numbers: item.serial_numbers || [],
        is_bulk_sale: item.is_bulk_sale || false,
        original_price: item.original_price ? parseFloat(item.original_price) : null,
        bulk_discount_percentage: item.bulk_discount_percentage ? parseFloat(item.bulk_discount_percentage) : null
      })),
      discount_type: discount.type,
      discount_value: parseFloat(discount.value) || 0,
      payment_method: payment.method,
      amount_paid: parseFloat(payment.amountPaid || 0),
      change_amount: parseFloat(payment.change || 0),
      tax_rate: parseFloat(taxRate) || 0,
      notes: `POS Transaction - ${new Date().toLocaleString()}`,
      pos_number: currentInvoiceNumber
    };

    console.log('ðŸ“¤ Sending payment data:', JSON.stringify(posData, null, 2));

    try {
      const response = await createPOS(posData);
      
      if (response.success) {
        setSuccess('Transaction completed successfully!');
        
        const receiptData = {
          ...response.data,
          user_name: response.data?.user_name || getCurrentCashierName(),
          cashier_name: response.data?.user_name || getCurrentCashierName(),
          payment_method: payment.method,
          customer: selectedCustomer.name,
          items: cart,
          subtotal: subtotal,
          discount: discountAmount,
          tax: taxAmount,
          total: totalAmount,
          paid: payment.amountPaid,
          change: payment.change,
          date: new Date().toISOString(),
          receipt_number: currentInvoiceNumber,
          pos_number: currentInvoiceNumber,
          status: 'paid'
        };
        
        setPendingReceiptData(receiptData);
        setPaymentStatusModalOpen(true);
        
        setCart([]);
        setPayment({
          method: 'cash',
          amountPaid: '',
          change: 0,
          chequeDetails: null
        });
        
        setPaymentDialogOpen(false);
        
        const newInvoiceNumber = generateInvoiceNumber();
        setInvoiceNumber(newInvoiceNumber);
        
        await refreshAfterSale();
        
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('âŒ Transaction failed:', err);
      setError(err.error ? `${err.message}: ${err.error}` : (err.message || 'Transaction failed'));
    } finally {
      setLoading(false);
    }
  };

  const refreshAfterSale = async () => {
    console.log('ðŸ”„ Auto-refreshing data after sale...');
    try {
      await Promise.all([
        fetchProducts(),
        fetchCustomers(),
        fetchStats()
      ]);
      console.log('âœ… Data refreshed successfully');
    } catch (err) {
      console.error('âŒ Error refreshing data:', err);
    }
  };

  const handleCreditPaymentSuccess = (transactionData, invoiceNumber, dueDate) => {
    setSuccess('Credit transaction completed successfully!');
    
    if (selectedCreditCustomer) {
      const newOutstanding = (selectedCreditCustomer.outstanding_balance || 0) + totalAmount;
      const newAvailable = (selectedCreditCustomer.available_credit || 0) - totalAmount;
      
      setCustomers(prev => 
        prev.map(c => 
          c.id === selectedCreditCustomer.id ? {
            ...c,
            outstanding_balance: newOutstanding,
            available_credit: newAvailable
          } : c
        )
      );
      
      if (selectedCustomer.id === selectedCreditCustomer.id) {
        setSelectedCustomer(prev => ({
          ...prev,
          outstanding_balance: newOutstanding,
          available_credit: newAvailable
        }));
      }
    }
    
    const receiptData = {
      ...transactionData,
      user_name: transactionData?.user_name || transactionData?.data?.user_name || getCurrentCashierName(),
      cashier_name: transactionData?.user_name || transactionData?.data?.user_name || getCurrentCashierName(),
      payment_method: 'credit',
      customer: selectedCustomer.name,
      items: cart,
      subtotal: subtotal,
      discount: discountAmount,
      tax: taxAmount,
      total: totalAmount,
      paid: 0,
      change: 0,
      date: new Date().toISOString(),
      receipt_number: invoiceNumber,
      pos_number: invoiceNumber,
      credit_reference: transactionData.transaction?.reference_number || 'N/A',
      due_date: dueDate ? new Date(dueDate).toLocaleDateString() : 'N/A'
    };
    
    setPendingReceiptData(receiptData);
    setPaymentStatusModalOpen(true);
    
    setCart([]);
    setPayment({
      method: 'cash',
      amountPaid: '',
      change: 0,
      chequeDetails: null
    });
    
    setPaymentDialogOpen(false);
    
    const newInvoiceNumber = generateInvoiceNumber();
    setInvoiceNumber(newInvoiceNumber);
    
    fetchStats();
    fetchCustomers();
    
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePaymentStatusConfirm = (status) => {
    if (pendingReceiptData) {
      const updatedReceiptData = {
        ...pendingReceiptData,
        status: status
      };
      
      printReceipt(updatedReceiptData);
      setPendingReceiptData(null);
    }
  };

  // Bulk Sale Functions - FIXED with serial handling
  const handleAddToBulkSale = (product) => {
    if (product.is_active === false) {
      setError('Cannot add inactive product to bulk sale');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const availableGoodStock = Math.max(0, product.good_quantity || 0);
    if (availableGoodStock <= 0) {
      setError('No good stock available for this product');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSelectedBulkProducts(prev => {
      const existing = prev.find(p => p.id === product.id);
      if (existing) {
        if (existing.quantity + 1 > availableGoodStock) {
          setError(`Only ${availableGoodStock} good items available in stock for ${product.name}`);
          setTimeout(() => setError(''), 3000);
          return prev;
        }
        return prev.map(p =>
          p.id === product.id
            ? { ...p, quantity: p.quantity + 1, line_total: p.price * (p.quantity + 1), selected_serials: [] }
            : p
        );
      } else {
        return [...prev, {
          ...product,
          quantity: 1,
          line_total: product.price,
          good_stock: availableGoodStock,
          selected_serials: []
        }];
      }
    });
  };

  const updateBulkQuantity = (id, delta) => {
    setSelectedBulkProducts(prev =>
      prev.map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + delta;
          if (newQuantity < 1) return null;
          
          const product = products.find(p => p.id === item.id);
          const availableGoodStock = Math.max(0, product?.good_quantity || 0);
          
          if (newQuantity > availableGoodStock) {
            setError(`Only ${availableGoodStock} good items available in stock for ${item.name}`);
            setTimeout(() => setError(''), 3000);
            return item;
          }
          
          // Clear selected serials if quantity changed
          const selectedSerials = (delta !== 0 && item.track_serials) ? [] : item.selected_serials;
          
          return {
            ...item,
            quantity: newQuantity,
            line_total: item.price * newQuantity,
            selected_serials: selectedSerials
          };
        }
        return item;
      }).filter(item => item !== null)
    );
  };

  const removeFromBulkSale = (id) => {
    setSelectedBulkProducts(prev => prev.filter(item => item.id !== id));
  };

  const calculateBulkTotal = () => {
    return selectedBulkProducts.reduce((sum, item) => sum + item.line_total, 0);
  };

  // ============ FIXED: Handle confirm bulk sale with serial selection ============
  const handleConfirmBulkSale = () => {
    if (selectedBulkProducts.length === 0) {
      setError('Please add products to bulk sale');
      return;
    }

    if (!bulkSalePrice || parseFloat(bulkSalePrice) <= 0) {
      setError('Please enter a valid bulk sale price');
      return;
    }

    // Store the bulk products and price
    setPendingBulkProducts(selectedBulkProducts);
    setPendingBulkPrice(bulkSalePrice);
    setPendingBulkSerials([]);
    setPendingBulkProductIndex(0);

    // Find first product that needs serials and hasn't been selected yet
    const productsNeedingSerials = selectedBulkProducts.filter(p => 
      productNeedsSerials(p) && (!p.selected_serials || p.selected_serials.length < p.quantity)
    );
    
    if (productsNeedingSerials.length > 0) {
      // Close the bulk dialog and start serial selection
      setBulkSaleDialogOpen(false);
      const firstProduct = productsNeedingSerials[0];
      setPendingBulkProduct(firstProduct);
      setPendingBulkQuantity(firstProduct.quantity);
      setBulkSerialSelectionOpen(true);
    } else {
      // No serials needed, process directly
      processBulkSale(selectedBulkProducts, bulkSalePrice);
      setBulkSaleDialogOpen(false);
    }
  };

  // ============ FIXED: Process bulk sale with serial numbers ============
  const processBulkSale = (products, price, serialsOverride = null) => {
    const bulkPrice = parseFloat(price);
    const totalOriginalPrice = products.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const ratio = bulkPrice / totalOriginalPrice;
    const serialPool = serialsOverride || pendingBulkSerials;

    products.forEach(product => {
      const adjustedPrice = product.price * ratio;
      const discountPercentage = 100 - (ratio * 100);
      
      // Prefer serials stored on product, then override/pending pool
      const fromProduct = Array.isArray(product.selected_serials) ? product.selected_serials : [];
      const fromPool = serialPool
        .filter((s) => s.product_id === product.id)
        .map((s) => s.serial_number);
      const serialNumbers = fromProduct.length > 0 ? fromProduct : fromPool;
      
      const newItem = {
        id: Date.now() + product.id,
        product_id: product.id,
        name: product.name,
        product_name: product.name,
        product_code: product.product_code,
        quantity: product.quantity,
        // unit_price already includes bulk discount — do not also set discount_percentage
        unit_price: adjustedPrice,
        discount_percentage: 0,
        tax_rate: taxRate,
        line_total: adjustedPrice * product.quantity,
        stock: product.quantity,
        good_stock: Math.max(0, product.good_quantity || 0),
        warranty_months: product.warranty_months || 0,
        warranty_terms: product.warranty_terms || '',
        serial_numbers: serialNumbers,
        is_bulk_sale: true,
        original_price: product.price,
        bulk_discount_percentage: discountPercentage > 0 ? Number(discountPercentage.toFixed(2)) : 0,
        track_serials: product.track_serials || false
      };
      setCart(prevCart => [...prevCart, newItem]);
    });

    const totalItems = products.reduce((sum, item) => sum + item.quantity, 0);
    setSuccess(`Added ${totalItems} items (${products.length} products) as bulk sale`);
    setTimeout(() => setSuccess(''), 3000);
    
    // Clear bulk sale state
    setSelectedBulkProducts([]);
    setBulkSalePrice('');
    setPendingBulkProducts([]);
    setPendingBulkPrice('');
    setPendingBulkSerials([]);
    setPendingBulkProductIndex(0);
  };

  const isProductAvailableForPOS = (product) => {
    if (product.is_active === false) return false;
    if (!(product.name || '').trim()) return false;
    const availableGoodStock = Math.max(0, product.good_quantity || 0);
    return availableGoodStock > 0;
  };

  const productMatchesSearch = (product, term) => {
    if (!term) return true;
    const lowerTerm = term.toLowerCase();
    const matchesSerial = product.serials && Array.isArray(product.serials) &&
      product.serials.some(s => (s.serial_number || '').toLowerCase().includes(lowerTerm));
    return (
      (product.name && product.name.toLowerCase().includes(lowerTerm)) ||
      (product.product_code && product.product_code.toLowerCase().includes(lowerTerm)) ||
      (product.barcode && product.barcode.toLowerCase().includes(lowerTerm)) ||
      matchesSerial ||
      (product.category && product.category.toLowerCase().includes(lowerTerm))
    );
  };

  const filteredProducts = products.filter(product =>
    isProductAvailableForPOS(product) &&
    productMatchesSearch(product, searchTerm.trim())
  );

  const inactiveProductsCount = products.filter(p => p.is_active === false).length;
  const outOfStockProductsCount = products.filter(p => {
    const goodStock = Math.max(0, p.good_quantity || 0);
    return goodStock <= 0 && p.is_active !== false;
  }).length;

  // Edit Item Dialog Component
  const EditItemDialog = () => {
    const [formData, setFormData] = useState({
      product_name: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      warranty_months: 0,
      warranty_terms: '',
      serial_numbers_input: ''
    });

    useEffect(() => {
      if (currentEditItem) {
        const serialsString = currentEditItem.serial_numbers?.join(', ') || '';
        
        setFormData({
          product_name: currentEditItem.product_name || currentEditItem.name || '',
          quantity: currentEditItem.quantity || 1,
          unit_price: currentEditItem.unit_price || 0,
          discount_percentage: currentEditItem.discount_percentage || 0,
          warranty_months: currentEditItem.warranty_months || 0,
          warranty_terms: currentEditItem.warranty_terms || '',
          serial_numbers_input: serialsString
        });
      }
    }, [currentEditItem]);

    const calculateTotal = () => {
      const quantity = parseFloat(formData.quantity) || 0;
      const unitPrice = parseFloat(formData.unit_price) || 0;
      const discountPercent = parseFloat(formData.discount_percentage) || 0;
      const subtotal = quantity * unitPrice;
      const discountAmount = subtotal * (discountPercent / 100);
      return subtotal - discountAmount;
    };

    const handleSave = () => {
      const serialNumbers = formData.serial_numbers_input
        .split(',')
        .map(sn => sn.trim())
        .filter(sn => sn !== '');

      if (currentEditItem.sell_with_serials && serialNumbers.length !== parseInt(formData.quantity)) {
        setError(`Serial numbers count (${serialNumbers.length}) must match quantity (${formData.quantity})`);
        setTimeout(() => setError(''), 3000);
        return;
      }

      const updatedItem = {
        ...currentEditItem,
        name: formData.product_name.trim() || currentEditItem.name,
        product_name: formData.product_name.trim() || currentEditItem.name,
        quantity: parseInt(formData.quantity) || 1,
        unit_price: parseFloat(formData.unit_price) || 0,
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        line_total: calculateTotal(),
        warranty_months: parseInt(formData.warranty_months) || 0,
        warranty_terms: formData.warranty_terms.trim(),
        serial_numbers: serialNumbers
      };

      updateCartItem(updatedItem);
    };

    if (!currentEditItem) return null;

    return (
      <Modal
        open={editItemDialogOpen}
        onClose={() => setEditItemDialogOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={editItemDialogOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            maxWidth: '90vw',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Edit Item</Typography>
              <IconButton onClick={() => setEditItemDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Product Name"
                  value={formData.product_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  inputProps={{ min: 1, max: currentEditItem.good_stock }}
                  helperText={`Good Stock: ${currentEditItem.good_stock}`}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Unit Price (LKR)"
                  type="number"
                  value={formData.unit_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit_price: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">LKR</InputAdornment> }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Discount %"
                  type="number"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.target.value }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    sx={{ width: '30%' }}
                    label="Warranty (Months)"
                    type="number"
                    value={formData.warranty_months}
                    onChange={(e) => setFormData(prev => ({ ...prev, warranty_months: e.target.value }))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">months</InputAdornment>,
                    }}
                  />
                  <TextField
                    sx={{ width: '70%' }}
                    label="Warranty Terms"
                    multiline
                    rows={1}
                    value={formData.warranty_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, warranty_terms: e.target.value }))}
                    placeholder="e.g., Parts & Labor, Carry-in, On-site"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SerialIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="primary">
                    Serial Numbers {formData.quantity > 1 ? `(Need ${formData.quantity} serial numbers)` : ''}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.serial_numbers_input}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_numbers_input: e.target.value }))}
                  placeholder={formData.quantity > 1 
                    ? `Enter ${formData.quantity} serial numbers separated by commas (e.g., SN001, SN002, SN003)` 
                    : "Enter serial number (e.g., SN001)"}
                  helperText={`${formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length} of ${formData.quantity} serial numbers entered`}
                  error={formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length > 0 && 
                         formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length !== parseInt(formData.quantity)}
                  InputProps={{
                    readOnly: currentEditItem.track_serials && formData.serial_numbers_input ? true : false
                  }}
                  sx={currentEditItem.track_serials && formData.serial_numbers_input ? { bgcolor: '#f5f5f5' } : {}}
                />
                {currentEditItem.track_serials && formData.serial_numbers_input && (
                  <Typography variant="caption" color="success.main">
                    âœ“ Serial numbers pre-selected from available stock
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Item Total: LKR {calculateTotal().toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formData.quantity} Ã— LKR {formData.unit_price} 
                    {formData.discount_percentage > 0 ? ` - ${formData.discount_percentage}% discount` : ''}
                  </Typography>
                  
                  {(formData.warranty_months > 0 || formData.warranty_terms || formData.serial_numbers_input) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ccc' }}>
                      {formData.warranty_months > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarrantyIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                          <Typography variant="body2" color="success.main">
                            <strong>{formData.warranty_months} months warranty</strong>
                            {formData.warranty_terms && ` - ${formData.warranty_terms}`}
                          </Typography>
                        </Box>
                      )}
                      
                      {formData.serial_numbers_input && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <SerialIcon fontSize="small" sx={{ color: '#1976d2', mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" color="info.main">
                              <strong>Serial Numbers:</strong>
                            </Typography>
                            <Typography variant="body2" color="info.main" sx={{ fontFamily: 'monospace' }}>
                              {formData.serial_numbers_input.split(',').map(sn => sn.trim()).filter(sn => sn).join(' â€¢ ')}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={() => setEditItemDialogOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                variant="contained" 
                startIcon={<SaveIcon />}
                disabled={currentEditItem.sell_with_serials && 
                         formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length !== parseInt(formData.quantity)}
              >
                Save Item
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  };

  // Bulk Sale Dialog Component - FIXED
  const BulkSaleDialog = () => {
    const [localSelectedProducts, setLocalSelectedProducts] = useState([]);
    const [localBulkSalePrice, setLocalBulkSalePrice] = useState('');
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [localSerialOpen, setLocalSerialOpen] = useState(false);
    const [localSerialProduct, setLocalSerialProduct] = useState(null);
    const [localSerialQty, setLocalSerialQty] = useState(1);

    useEffect(() => {
      if (bulkSaleDialogOpen) {
        setLocalSelectedProducts(selectedBulkProducts);
        setLocalBulkSalePrice(bulkSalePrice);
        setLocalSearchTerm(searchBulkTerm);
      }
    }, [bulkSaleDialogOpen, selectedBulkProducts, bulkSalePrice, searchBulkTerm]);

    const filteredBulkProducts = products.filter(product =>
      isProductAvailableForPOS(product) && (
        product.name.toLowerCase().includes(localSearchTerm.toLowerCase()) ||
        (product.product_code || '').toLowerCase().includes(localSearchTerm.toLowerCase()) ||
        (product.barcode || '').toLowerCase().includes(localSearchTerm.toLowerCase())
      )
    );

    const openLocalSerialPicker = (productRow) => {
      setLocalSerialProduct(productRow);
      setLocalSerialQty(productRow.quantity || 1);
      setLocalSerialOpen(true);
    };

    const handleLocalSerialConfirm = (selectedSerials) => {
      const serialNumbers = selectedSerials.map((s) => s.serial_number);
      setLocalSelectedProducts((prev) =>
        prev.map((p) =>
          p.id === localSerialProduct?.id
            ? { ...p, selected_serials: serialNumbers, track_serials: true }
            : p
        )
      );
      setLocalSerialOpen(false);
      setLocalSerialProduct(null);
    };

    const handleAddToBulkSaleLocal = (product) => {
      if (product.is_active === false) {
        setError('Cannot add inactive product to bulk sale');
        return;
      }
      const availableGoodStock = Math.max(0, product.good_quantity || 0);
      if (availableGoodStock <= 0) {
        setError('No good stock available for this product');
        return;
      }

      let nextRow = null;
      setLocalSelectedProducts(prev => {
        const existing = prev.find(p => p.id === product.id);
        if (existing) {
          if (existing.quantity + 1 > availableGoodStock) {
            setError(`Only ${availableGoodStock} good items available in stock for ${product.name}`);
            setTimeout(() => setError(''), 3000);
            return prev;
          }
          nextRow = {
            ...existing,
            quantity: existing.quantity + 1,
            line_total: existing.price * (existing.quantity + 1),
            selected_serials: []
          };
          return prev.map(p => (p.id === product.id ? nextRow : p));
        }
        nextRow = {
          ...product,
          quantity: 1,
          line_total: product.price,
          good_stock: availableGoodStock,
          selected_serials: []
        };
        return [...prev, nextRow];
      });

      // Popup serial picker when product has serial numbers
      if (productNeedsSerials(product) && nextRow) {
        setTimeout(() => openLocalSerialPicker(nextRow), 0);
      }
    };

    const updateBulkQuantityLocal = (id, delta) => {
      setLocalSelectedProducts(prev =>
        prev.map(item => {
          if (item.id === id) {
            const newQuantity = item.quantity + delta;
            if (newQuantity < 1) return null;
            
            const product = products.find(p => p.id === item.id);
            const availableGoodStock = Math.max(0, product?.good_quantity || 0);
            
            if (newQuantity > availableGoodStock) {
              setError(`Only ${availableGoodStock} good items available in stock for ${item.name}`);
              setTimeout(() => setError(''), 3000);
              return item;
            }
            
            // Clear selected serials if quantity changed
            const selectedSerials = (delta !== 0 && item.track_serials) ? [] : item.selected_serials;
            
            return {
              ...item,
              quantity: newQuantity,
              line_total: item.price * newQuantity,
              selected_serials: selectedSerials
            };
          }
          return item;
        }).filter(item => item !== null)
      );
    };

    const removeFromBulkSaleLocal = (id) => {
      setLocalSelectedProducts(prev => prev.filter(item => item.id !== id));
    };

    const calculateBulkTotalLocal = () => {
      return localSelectedProducts.reduce((sum, item) => sum + item.line_total, 0);
    };

    const handleConfirmBulkSaleLocal = () => {
      if (localSelectedProducts.length === 0) {
        setError('Please add products to bulk sale');
        return;
      }
      if (!localBulkSalePrice || parseFloat(localBulkSalePrice) <= 0) {
        setError('Please enter a valid bulk sale price');
        return;
      }

      const missingSerials = localSelectedProducts.find(
        (p) =>
          productNeedsSerials(p) &&
          (!p.selected_serials || p.selected_serials.length !== p.quantity)
      );
      if (missingSerials) {
        setError(`Select ${missingSerials.quantity} serial number(s) for ${missingSerials.name}`);
        openLocalSerialPicker(missingSerials);
        return;
      }

      setSelectedBulkProducts(localSelectedProducts);
      setBulkSalePrice(localBulkSalePrice);

      const serialObjects = localSelectedProducts.flatMap((p) =>
        (p.selected_serials || []).map((sn, i) => ({
          id: `${p.id}-${i}`,
          serial_number: sn,
          product_id: p.id
        }))
      );

      processBulkSale(localSelectedProducts, localBulkSalePrice, serialObjects);
      setBulkSaleDialogOpen(false);
    };

    const handleClose = () => {
      setSelectedBulkProducts([]);
      setBulkSalePrice('');
      setSearchBulkTerm('');
      setBulkSaleDialogOpen(false);
      setLocalSerialOpen(false);
    };

    return (
      <>
      <Dialog
        open={bulkSaleDialogOpen}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalOfferIcon color="secondary" />
            <Typography variant="h6">Bulk Sale</Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            pt: 1
          }}
        >
          <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, height: { md: '100%' } }}>
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: { xs: 'none', md: '100%' } }}>
              <Paper sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Select Products
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search products..."
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2, flexShrink: 0 }}
                />
                <Box
                  sx={{
                    flex: 1,
                    minHeight: { xs: 280, md: 0 },
                    maxHeight: { xs: 360, md: 'none' },
                    overflowY: 'scroll',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1,
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {filteredBulkProducts.map(product => {
                    const isSelected = localSelectedProducts.some(p => p.id === product.id);
                    const selectedProduct = isSelected ? localSelectedProducts.find(p => p.id === product.id) : null;
                    const isActive = product.is_active !== false;
                    const availableGoodStock = Math.max(0, product.good_quantity || 0);
                    const hasGoodStock = availableGoodStock > 0;
                    
                    return (
                      <Paper
                        key={product.id}
                        sx={{
                          p: 2,
                          mb: 1,
                          cursor: isActive && hasGoodStock ? 'pointer' : 'default',
                          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          opacity: (!isActive || !hasGoodStock) ? 0.7 : 1,
                          position: 'relative'
                        }}
                        onClick={() => {
                          if (isActive && hasGoodStock) {
                            handleAddToBulkSaleLocal(product);
                          }
                        }}
                      >
                        {!isActive && (
                          <Chip
                            label="INACTIVE"
                            size="small"
                            color="warning"
                            icon={<InactiveIcon />}
                            sx={{ position: 'absolute', top: 5, right: 5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {isActive && !hasGoodStock && (
                          <Chip
                            label="NO GOOD STOCK"
                            size="small"
                            color="error"
                            icon={<WarningIcon />}
                            sx={{ position: 'absolute', top: 5, right: 5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Code: {product.product_code}
                            </Typography>
                            {product.warranty_months > 0 && (
                              <Chip
                                label={`${product.warranty_months}m Warranty`}
                                size="small"
                                color="info"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                icon={<WarrantyIcon fontSize="small" />}
                              />
                            )}
                            {productNeedsSerials(product) && (
                              <Chip
                                label="Has Serials"
                                size="small"
                                color="primary"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                icon={<SerialIcon fontSize="small" />}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            LKR {product.price.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color={availableGoodStock <= (product.reorder_level || 10) ? 'warning.main' : 'text.secondary'}>
                            Good Stock: {availableGoodStock}
                          </Typography>
                          {isSelected && selectedProduct && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateBulkQuantityLocal(product.id, -1);
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="body2">
                                Qty: {selectedProduct.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateBulkQuantityLocal(product.id, 1);
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: { xs: 'none', md: '100%' } }}>
              <Paper sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ flexShrink: 0 }}>
                  Bulk Sale Summary
                </Typography>
                {localSelectedProducts.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <LocalOfferIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                    <Typography color="text.secondary">No products selected</Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer
                      sx={{
                        flex: 1,
                        minHeight: { xs: 220, md: 0 },
                        maxHeight: { xs: 320, md: 'none' },
                        overflowY: 'scroll',
                        mb: 2,
                        WebkitOverflowScrolling: 'touch'
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="center">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="center">Serials</TableCell>
                            <TableCell align="center">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {localSelectedProducts.map(item => {
                            const needsSn = productNeedsSerials(item);
                            const snCount = Array.isArray(item.selected_serials) ? item.selected_serials.length : 0;
                            const snOk = !needsSn || snCount === item.quantity;
                            return (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="body2">{item.name}</Typography>
                                {item.warranty_months > 0 && (
                                  <Chip
                                    label={`${item.warranty_months}m`}
                                    size="small"
                                    color="info"
                                    sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                                    icon={<WarrantyIcon fontSize="small" />}
                                  />
                                )}
                                {needsSn && (
                                  <Chip
                                    label="Serials"
                                    size="small"
                                    color="primary"
                                    sx={{ height: 18, fontSize: '0.65rem', mt: 0.5, ml: 0.5 }}
                                    icon={<SerialIcon fontSize="small" />}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box display="flex" alignItems="center" justifyContent="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      updateBulkQuantityLocal(item.id, -1);
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ mx: 1, minWidth: 20 }}>
                                    {item.quantity}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      updateBulkQuantityLocal(item.id, 1);
                                      if (needsSn) {
                                        setTimeout(() => {
                                          setLocalSelectedProducts((prev) => {
                                            const row = prev.find((p) => p.id === item.id);
                                            if (row) openLocalSerialPicker(row);
                                            return prev;
                                          });
                                        }, 0);
                                      }
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  LKR {item.line_total.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  @ LKR {item.price.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {needsSn ? (
                                  <Button
                                    size="small"
                                    variant={snOk ? 'outlined' : 'contained'}
                                    color={snOk ? 'success' : 'warning'}
                                    startIcon={<SerialIcon />}
                                    onClick={() => openLocalSerialPicker(item)}
                                  >
                                    {snOk ? `${snCount}/${item.quantity}` : `Select (${snCount}/${item.quantity})`}
                                  </Button>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeFromBulkSaleLocal(item.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">Total Items:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" fontWeight="bold">
                            {localSelectedProducts.reduce((sum, item) => sum + item.quantity, 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Products:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" fontWeight="bold">
                            {localSelectedProducts.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Original Total:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" fontWeight="bold">
                            LKR {calculateBulkTotalLocal().toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <TextField
                      fullWidth
                      label="Bulk Sale Price (LKR)"
                      type="number"
                      value={localBulkSalePrice}
                      onChange={(e) => setLocalBulkSalePrice(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        Discount: {calculateBulkTotalLocal() > 0 && localBulkSalePrice ? 
                          `${((1 - parseFloat(localBulkSalePrice) / calculateBulkTotalLocal()) * 100).toFixed(2)}%` : 
                          '0%'}
                      </Typography>
                    </Alert>
                    {localSelectedProducts.some((p) => productNeedsSerials(p)) && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <SerialIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          Select serial numbers for each serial-tracked product (click Select / serial button).
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBulkSaleLocal}
            variant="contained"
            color="secondary"
            disabled={localSelectedProducts.length === 0 || !localBulkSalePrice || parseFloat(localBulkSalePrice) <= 0}
            startIcon={<LocalOfferIcon />}
          >
            Add to Cart ({localSelectedProducts.reduce((sum, item) => sum + item.quantity, 0)} items)
          </Button>
        </DialogActions>
      </Dialog>

      <BulkSerialSelectionModal
        open={localSerialOpen}
        onClose={() => {
          setLocalSerialOpen(false);
          setLocalSerialProduct(null);
        }}
        product={localSerialProduct}
        onSelect={handleLocalSerialConfirm}
        quantity={localSerialQty}
      />
      </>
    );
  };

  const creditCustomers = customers.filter(c => c.is_credit_customer);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%)'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="primary.dark">
              Point of Sale
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cashier: <strong>{getCurrentUserName()}</strong> · Invoice {invoiceNumber}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button variant="contained" onClick={() => navigate('/pos/sales')}>
              Sales history
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => navigate('/office-issue')}>
              Office issue
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                Products
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => navigate('/office-issue')}
                >
                  Issue Sun Systems Office
                </Button>
                <Tooltip title={`${inactiveProductsCount} inactive products`}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<InactiveIcon />}
                    onClick={() => {
                      setSearchTerm('');
                      const inactiveProducts = products.filter(p => p.is_active === false);
                      if (inactiveProducts.length > 0) {
                        setError(`${inactiveProducts.length} inactive products found. They cannot be added to cart.`);
                      }
                    }}
                  >
                    Inactive ({inactiveProductsCount})
                  </Button>
                </Tooltip>
                <Tooltip title={`${outOfStockProductsCount} products with no good stock`}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<WarningIcon />}
                    onClick={() => {
                      setSearchTerm('');
                      const outOfStockProducts = products.filter(p => {
                        const goodStock = Math.max(0, p.good_quantity || 0);
                        return goodStock <= 0 && p.is_active !== false;
                      });
                      if (outOfStockProducts.length > 0) {
                        setError(`${outOfStockProducts.length} products with no good stock found. They cannot be added to cart.`);
                      }
                    }}
                  >
                    No Good Stock ({outOfStockProductsCount})
                  </Button>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchProducts}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/sales')}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<LocalOfferIcon />}
                  onClick={() => setBulkSaleDialogOpen(true)}
                >
                  Bulk Sale
                </Button>
              </Box>
            </Box>

            {/* Product Status Summary */}
            {(inactiveProductsCount > 0 || outOfStockProductsCount > 0) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  {inactiveProductsCount > 0 && (
                    <Chip
                      icon={<InactiveIcon />}
                      label={`${inactiveProductsCount} Inactive Products`}
                      size="small"
                      color="warning"
                    />
                  )}
                  {outOfStockProductsCount > 0 && (
                    <Chip
                      icon={<WarningIcon />}
                      label={`${outOfStockProductsCount} No Good Stock`}
                      size="small"
                      color="error"
                    />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Inactive and out of stock products cannot be added to cart
                  </Typography>
                </Box>
              </Alert>
            )}

            {/* Customer Selection */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: selectedCustomer.is_credit_customer ? '#e8f4fd' : 'primary.light', color: selectedCustomer.is_credit_customer ? '#007bff' : 'white' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">
                      Customer: <strong>{selectedCustomer.name}</strong>
                    </Typography>
                    {selectedCustomer.is_credit_customer && (
                      <Chip
                        label="CREDIT"
                        size="small"
                        color="primary"
                        icon={<CreditCardIcon />}
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2">
                    {selectedCustomer.phone || 'No phone'}
                  </Typography>
                  {selectedCustomer.is_credit_customer && (
                    <Box display="flex" gap={2} sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        Credit Limit: {formatCurrency(selectedCustomer.credit_limit)}
                      </Typography>
                      <Typography variant="caption">
                        Available: {formatCurrency(selectedCustomer.available_credit)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Autocomplete
                    sx={{ width: 300 }}
                    options={customers}
                    getOptionLabel={(option) => `${option.name} ${option.is_credit_customer ? 'ðŸ’³' : ''} (${option.phone || 'No phone'})`}
                    value={selectedCustomer.id !== 1 ? customers.find(c => c.id === selectedCustomer.id) : null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedCustomer({
                          id: newValue.id,
                          name: newValue.name,
                          phone: newValue.phone,
                          is_credit_customer: newValue.is_credit_customer || false,
                          credit_limit: newValue.credit_limit || 0,
                          available_credit: newValue.available_credit || 0
                        });
                      } else {
                        setSelectedCustomer({
                          id: 1,
                          name: 'Walk-in Customer',
                          is_credit_customer: false,
                          credit_limit: 0,
                          available_credit: 0
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Customer"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setCustomerDialogOpen(true)}
                  >
                    New Customer
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Stats Cards */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Today's Sales
                    </Typography>
                    <Typography variant="h6">
                      {stats.today?.sales || '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Today's Transactions
                    </Typography>
                    <Typography variant="h6">
                      {stats.today?.transactions || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Monthly Sales
                    </Typography>
                    <Typography variant="h6">
                      {stats.monthly?.sales || '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Cart Items
                    </Typography>
                    <Typography variant="h6">
                      {cart.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Search Input */}
            <TextField
              fullWidth
              placeholder="Scan barcode, serial number, or search products..."
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={handleBarcodeKeyPress}
              inputRef={barcodeInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            {/* Products Grid */}
            <Box sx={{ height: 'calc(100vh - 500px)', minHeight: '400px', overflowY: 'auto', pr: 1 }}>
              {filteredProducts.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    {searchTerm.trim()
                      ? 'No available products match your search.'
                      : 'No products with good stock available.'}
                  </Typography>
                </Box>
              ) : (
              <Grid container spacing={2}>
                {filteredProducts.map((product) => {
                  const isActive = product.is_active !== false;
                  const availableGoodStock = Math.max(0, product.good_quantity || 0);
                  const hasGoodStock = availableGoodStock > 0;
                  const serialCount = countAvailableSerials(product);
                  const nonSerialCount = getNonSerialStock(product);
                  const hasSerials = serialCount > 0;
                  const mixed = serialCount > 0 && nonSerialCount > 0;
                  
                  return (
                    <Grid item xs={12} key={product.id}>
                      <Tooltip title={
                        !isActive ? 'Product is inactive - Cannot add to cart' :
                        !hasGoodStock ? `No good stock available - Good stock: ${availableGoodStock}` :
                        hasSerials ? `Available: ${availableGoodStock} good · Serial: ${serialCount} · Non-serial: ${nonSerialCount}` :
                        `Available: ${availableGoodStock} good units | Price: LKR ${product.price.toFixed(2)}`
                      }>
                        <Paper
                          sx={{
                            p: 1.5,
                            px: 2,
                            cursor: isActive && hasGoodStock ? 'pointer' : 'default',
                            border: !hasGoodStock ? '2px solid #ff6b6b' : !isActive ? '2px solid #ff9800' : hasSerials ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            opacity: (!isActive || !hasGoodStock) ? 0.7 : 1
                          }}
                          onClick={() => {
                            if (isActive && hasGoodStock) {
                              addToCart(product, 1);
                            }
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" noWrap>
                              {product.product_code}
                              {product.barcode ? ` · ${product.barcode}` : ''}
                              {product.category ? ` · ${product.category}` : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              LKR {product.price.toFixed(2)}
                            </Typography>
                            {!isActive && <Chip size="small" color="warning" label="INACTIVE" />}
                            {isActive && !hasGoodStock && <Chip size="small" color="error" label="NO STOCK" />}
                            {isActive && hasGoodStock && mixed && (
                              <Chip size="small" color="secondary" icon={<SerialIcon />} label="Serial / Non-serial" />
                            )}
                            {isActive && hasGoodStock && hasSerials && !mixed && (
                              <Chip size="small" color="primary" icon={<SerialIcon />} label="SERIALS" />
                            )}
                            {product.warranty_months > 0 && (
                              <Chip
                                icon={<WarrantyIcon fontSize="small" />}
                                label={`${product.warranty_months}m`}
                                size="small"
                                color="info"
                              />
                            )}
                            <Chip
                              label={`Good: ${availableGoodStock}`}
                              size="small"
                              color={availableGoodStock <= (product.reorder_level || 10) ? 'warning' : 'success'}
                            />
                          </Box>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Cart Column */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Cart ({cart.length} items)
              </Typography>
              <Badge badgeContent={cart.length} color="primary">
                <ReceiptIcon />
              </Badge>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Cart Items Table */}
            <TableContainer sx={{ flex: 1, mb: 2, maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="center">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">Cart is empty</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    cart.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                            {item.is_bulk_sale && (
                              <Chip label="Bulk" size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {item.discount_percentage > 0 && (
                              <Chip label={`${item.discount_percentage}%`} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {item.warranty_months > 0 && (
                              <Tooltip title={item.warranty_terms || `${item.warranty_months} months warranty`}>
                                <Chip 
                                  icon={<WarrantyIcon fontSize="small" />}
                                  label={`${item.warranty_months}m`} 
                                  size="small" 
                                  color="info" 
                                  sx={{ height: 20, fontSize: '0.7rem' }} 
                                />
                              </Tooltip>
                            )}
                            {item.serial_numbers && item.serial_numbers.length > 0 && (
                              <Tooltip title={`Serial: ${item.serial_numbers.join(', ')}`}>
                                <Chip 
                                  icon={<SerialIcon fontSize="small" />}
                                  label={`${item.serial_numbers.length} SN`} 
                                  size="small" 
                                  color="primary" 
                                  sx={{ height: 20, fontSize: '0.7rem' }} 
                                />
                              </Tooltip>
                            )}
                            {item.sell_with_serials && (!item.serial_numbers || item.serial_numbers.length === 0) && (
                              <Chip 
                                icon={<WarningIcon fontSize="small" />}
                                label="No Serial" 
                                size="small" 
                                color="warning" 
                                sx={{ height: 20, fontSize: '0.7rem' }} 
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <IconButton size="small" onClick={() => updateQuantity(item.id, -1)}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ mx: 1, minWidth: 30 }}>
                              {item.quantity}
                            </Typography>
                            <IconButton size="small" onClick={() => updateQuantity(item.id, 1)}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            LKR {item.line_total.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            LKR {item.unit_price.toFixed(2)} each
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => editItem(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            {/* Totals */}
            <List dense>
              <ListItem>
                <ListItemText primary="Subtotal" />
                <Typography fontWeight="bold">LKR {subtotal.toFixed(2)}</Typography>
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalOfferIcon fontSize="small" />
                      <Typography>Discount</Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        select
                        size="small"
                        value={discount.type}
                        onChange={(e) => setDiscount(prev => ({ ...prev, type: e.target.value }))}
                        sx={{ width: 100, mr: 1 }}
                      >
                        <MenuItem value="percentage">%</MenuItem>
                        <MenuItem value="fixed">LKR</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        type="number"
                        value={discount.value}
                        onChange={(e) => setDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                        sx={{ width: 100 }}
                      />
                    </Box>
                  }
                />
                <Typography color="error">- LKR {discountAmount.toFixed(2)}</Typography>
              </ListItem>
              <ListItem>
                <Box display="flex" alignItems="center" width="100%">
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Tax Rate (%)
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                    sx={{ width: 80 }}
                  />
                </Box>
              </ListItem>
              <ListItem>
                <ListItemText primary="Tax Amount" />
                <Typography>LKR {taxAmount.toFixed(2)}</Typography>
              </ListItem>
              <Divider />
              <ListItem sx={{ backgroundColor: 'primary.main', color: 'white', borderRadius: 1 }}>
                <ListItemText primary={<Typography variant="h6">TOTAL</Typography>} />
                <Typography variant="h6">LKR {totalAmount.toFixed(2)}</Typography>
              </ListItem>
            </List>

            {/* Action Buttons */}
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<PaymentIcon />}
                disabled={cart.length === 0 || loading}
                onClick={() => setPaymentDialogOpen(true)}
                sx={{ mb: 1, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'PROCESS PAYMENT'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => setCart([])}
                disabled={cart.length === 0}
              >
                Clear Cart
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Quantity Dialog */}
      <QuantityDialog
        open={quantityDialogOpen}
        onClose={() => {
          setQuantityDialogOpen(false);
          setSelectedProductForQuantity(null);
        }}
        product={selectedProductForQuantity}
        onConfirm={handleQuantityConfirm}
        availableStock={selectedProductForQuantity ? Math.max(0, selectedProductForQuantity.good_quantity || 0) : 0}
        serialStock={selectedProductForQuantity ? remainingSerialStock(selectedProductForQuantity) : 0}
        nonSerialStock={selectedProductForQuantity ? remainingNonSerialStock(selectedProductForQuantity) : 0}
      />

      {/* Serial Selection Modal */}
      <SerialSelectionModal
        open={serialSelectionOpen}
        onClose={() => {
          setSerialSelectionOpen(false);
          setPendingProduct(null);
        }}
        product={pendingProduct}
        onSelect={handleSerialSelection}
        quantity={pendingQuantity}
      />

      {/* Bulk Serial Selection Modal */}
      <BulkSerialSelectionModal
        open={bulkSerialSelectionOpen}
        onClose={() => {
          setBulkSerialSelectionOpen(false);
          setPendingBulkProduct(null);
        }}
        product={pendingBulkProduct}
        onSelect={handleBulkSerialSelection}
        quantity={pendingBulkQuantity}
      />

      {/* Payment Status Modal */}
      <PaymentStatusModal
        open={paymentStatusModalOpen}
        onClose={() => {
          setPaymentStatusModalOpen(false);
          setPendingReceiptData(null);
        }}
        onConfirm={handlePaymentStatusConfirm}
        transactionData={pendingReceiptData || {}}
      />

      {/* Credit Payment Modal */}
      {selectedCreditCustomer && (
        <CreditPaymentModal
          open={creditPaymentOpen}
          onClose={() => {
            setCreditPaymentOpen(false);
            setSelectedCreditCustomer(null);
          }}
          customer={selectedCreditCustomer}
          amount={totalAmount}
          onSuccess={handleCreditPaymentSuccess}
          onCreateCreditSale={createCreditSale}
          invoiceNumber={invoiceNumber}
        />
      )}

      {/* Cheque Payment Modal */}
      <ChequePaymentModal
        open={chequePaymentOpen}
        onClose={() => {
          setChequePaymentOpen(false);
          setPayment(prev => ({ ...prev, method: 'cash', amountPaid: '', change: 0, chequeDetails: null }));
        }}
        amount={totalAmount}
        customer={selectedCustomer}
        onProcessChequePayment={handleProcessChequePayment}
        invoiceNumber={invoiceNumber}
      />

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Payment Method"
                  select
                  value={payment.method}
                  onChange={(e) => handlePaymentChange('method', e.target.value)}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Credit/Debit Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="online">Online Payment</MenuItem>
                  <MenuItem value="cheque">
                    <Box display="flex" alignItems="center" gap={1}>
                      <ChequeIcon fontSize="small" />
                      <span>Cheque</span>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    value="credit"
                    disabled={!selectedCustomer.is_credit_customer || selectedCustomer.available_credit < totalAmount}
                  >
                    Credit {!selectedCustomer.is_credit_customer && '(Not available)'}
                  </MenuItem>
                </TextField>
              </Grid>

              {payment.method !== 'credit' && payment.method !== 'cheque' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                      <Typography variant="h5" fontWeight="bold">
                        LKR {totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Amount Paid"
                      type="number"
                      value={payment.amountPaid}
                      onChange={(e) => handlePaymentChange('amountPaid', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Change"
                      value={payment.change > 0 ? payment.change.toFixed(2) : '0.00'}
                      InputProps={{
                        readOnly: true,
                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      }}
                    />
                  </Grid>
                </>
              )}

              {payment.method === 'cheque' && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<ChequeIcon />}>
                    <Typography variant="body2" fontWeight="bold">
                      Cheque Payment Selected
                    </Typography>
                    <Typography variant="body2">
                      Amount: LKR {totalAmount.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      You will enter cheque details on the next screen.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {payment.method === 'credit' && selectedCustomer.is_credit_customer && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<CreditCardIcon />}>
                    <Typography variant="body2" fontWeight="bold">
                      Credit Payment Selected
                    </Typography>
                    <Typography variant="body2">
                      Amount: LKR {totalAmount.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Available Credit: {formatCurrency(selectedCustomer.available_credit)}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (payment.method === 'credit' && selectedCustomer.is_credit_customer) {
                setCreditPaymentOpen(true);
              } else if (payment.method === 'cheque') {
                setChequePaymentOpen(true);
              } else {
                processPayment();
              }
            }}
            disabled={loading || (payment.method !== 'credit' && payment.method !== 'cheque' && parseFloat(payment.amountPaid || 0) < totalAmount)}
          >
            {payment.method === 'credit' ? 'Process Credit' :
             payment.method === 'cheque' ? 'Enter Cheque Details' :
             'Complete Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Type"
                  select
                  value={newCustomer.customer_type}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, customer_type: e.target.value }))}
                >
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="corporate">Corporate</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Credit Limit (LKR)"
                  type="number"
                  value={newCustomer.credit_limit}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, credit_limit: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomer}
            disabled={!newCustomer.name.trim() || !newCustomer.phone.trim()}
          >
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      <EditItemDialog />
      <BulkSaleDialog />
    </Container>
  );
};

export default POS;
